import React, { useCallback, useEffect, useState } from 'react';
// STYLES
import { Paginator, TitleStep, SubtitleStep, HintStep, ProposalAlertWrapper, ProposalAlert } from '../styles';

// COMPONENTS
import {
  Alert,
  Divider,
  Flex,
  Form,
  formatCPF,
  formatPhoneNumber,
  parseCurrencyValue,
  Text,
} from '@bancobarigui/barihaus';

// UTILS
import logger from '../../../utils/logger.utils';
import { handleProposalError } from '../../../utils/apiErrorHandler';
import { getConsigneeFieldRules } from '../../../utils/consigneeRules';

// CONTEXT
import { useProposal } from '../../../hooks/AppContext';

// SERVICES
import ProposalService from '../../../services/ProposalService';

// COMPONENTS
import ModalProposalAlreadyRegistered from '../../../components/UI/Modal/BarihausModal/ModalProposalAlreadyRegistered';

// INTERFACE
import Proposal from '../../../interfaces/Proposal';
import { ProposalForm } from '../../../components/ProposalForm/ProposalForm';
import ProposalList from '../../../components/ProposalList/ProposalList';

interface IForm {
  formValues: Proposal;
  onChange: (e: any) => void;
}

interface ITodo {
  loanType: string;
  proposalNumber: string;
  purchaseMargin: number;
  documentsProposal: any;
}

interface IAlert {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  description?: React.ReactNode;
}

export const ProposalData = ({ onChange, formValues }: IForm) => {
  const { setNextStepDisabled, cnpjconsignees, setCnpjconsignees, consignCardPendence, validateAndExecuteMarginLimit } =
    useProposal();
  const [form] = Form.useForm();
  const [proposalSearchForm] = Form.useForm();

  const defaultValueConvenant = cnpjconsignees.find((val: any) => val.value == formValues?.cnpjConsigneeId);

  const [loan, setLoan] = useState<string>('');
  const [proposalNumber, setProposalNumber] = useState<string>('');
  const [purchaseMargin, setPurchaseMargin] = useState<number>(0);
  const [selectedLoanType, setSelectedLoanType] = useState<string>('');
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [isProposalSearched, setIsProposalSearched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [enableFields, setEnableFields] = useState<boolean>(false);
  const [disableClientFields, setDisableClientFields] = useState<boolean>(false);
  const [todos, setTodos] = useState<ITodo[]>([]);
  const [showAlert, setShowAlert] = useState<IAlert>({
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'warning' | 'info',
    description: '',
  });
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [originalClientData, setOriginalClientData] = useState<{
    name: string;
    documentNumber: string;
    cnpjConsigneeId: string;
  } | null>(null);

  // Variáveis para determinar quais campos são mostrados
  const { showEmailForInvoice, showExtraFields, showPurchaseMargin } = getConsigneeFieldRules(
    formValues.cnpjConsigneeId,
    formValues.hasConsignCard,
  );

  // Função para validar se todos os campos obrigatórios estão preenchidos
  const validateRequiredFields = useCallback(() => {
    // Campos sempre obrigatórios
    const hasName = !!(formValues.name && formValues.name.trim() !== '');
    const hasLoanType = !!(loan && loan.trim() !== '');
    const hasPhoneNumber = !!(formValues.phoneNumber && formValues.phoneNumber.trim() !== '');

    // Validação de email (obrigatório quando showEmailForInvoice é true)
    const emailValid = !showEmailForInvoice || !!(formValues.email && formValues.email.trim() !== '');

    // Validação de margem de compra (obrigatório quando showPurchaseMargin é true)
    const purchaseMarginValid = !showPurchaseMargin || purchaseMargin >= 0;

    // Validação de campos extras
    const extraFieldsValid =
      !showExtraFields ||
      (!!(formValues.companyDocument && formValues.companyDocument.trim() !== '') &&
        !!(formValues.companyLegalName && formValues.companyLegalName.trim() !== ''));

    // Todos os campos obrigatórios devem estar preenchidos
    return hasName && hasLoanType && hasPhoneNumber && emailValid && purchaseMarginValid && extraFieldsValid;
  }, [
    formValues.name,
    loan,
    formValues.phoneNumber,
    formValues.email,
    purchaseMargin,
    showEmailForInvoice,
    showPurchaseMargin,
    showExtraFields,
    formValues.companyDocument,
    formValues.companyLegalName,
  ]);

  // useEffect para atualizar o estado do botão sempre que os campos mudarem
  useEffect(() => {
    setIsButtonEnabled(validateRequiredFields());
  }, [validateRequiredFields]);

  // useEffect para controlar o botão "Próximo" baseado na quantidade de propostas
  useEffect(() => {
    const hasProposals = formValues.loanDetails && formValues.loanDetails.length > 0;
    setNextStepDisabled(!hasProposals);
  }, [formValues.loanDetails, setNextStepDisabled]);

  const handleChangeLoan = useCallback(
    (value) => {
      onChange({
        value: value,
        name: 'loanType',
      });
      setLoan(value);
    },
    [onChange],
  );

  const handleChangeProposalNumber = useCallback(
    (e) => {
      // Input do Barihaus passa um objeto com propriedade value, similar ao handleClientNameChange
      const value = e?.target?.value || e?.value || e || '';
      const previousValue = formValues.proposalNumber;
      onChange({
        value: value,
        name: 'proposalNumber',
      });
      setProposalNumber(value);
      // Se o usuário alterar o proposalNumber após uma busca bem-sucedida, limpar os dados preenchidos
      if (isProposalSearched && value !== previousValue) {
        // Limpar campos preenchidos pela API no formulário visual
        form.setFieldsValue({
          name: '',
          documentNumber: '',
          cnpjConsigneeId: '',
          phoneNumber: '',
          email: '',
          companyDocument: '',
          companyLegalName: '',
        });
        // Limpar campos no formValues através do onChange
        const fieldsToReset = [
          'name',
          'documentNumber',
          'cnpjConsigneeId',
          'phoneNumber',
          'email',
          'hasConsignCard',
          'limitMultiply',
          'cnpjConsigneeName',
          'companyDocument',
          'companyLegalName',
        ];
        fieldsToReset.forEach((fieldName) => {
          onChange({
            name: fieldName,
            value: fieldName === 'hasConsignCard' ? false : fieldName === 'limitMultiply' ? 0 : '',
          });
        });
        // Resetar estados relacionados à busca
        setIsProposalSearched(false);
        setEnableFields(false);
      }
      // Se limpar proposalNumber e não há propostas adicionadas, permitir que mude de cliente
      if (value === '' && formValues.loanDetails.length === 0) {
        setOriginalClientData(null);
        setIsProposalSearched(false);
        setEnableFields(false);
      }
    },
    [onChange, formValues.loanDetails.length, isProposalSearched, formValues.proposalNumber, form],
  );

  const handleChangePurchaseMargin = useCallback(
    (e) => {
      // Input do Barihaus passa um evento, extrair o valor do target
      let value = e?.target?.value || e?.value || e || '';
      // Garantir que seja sempre um número ou string, nunca um objeto
      if (typeof value === 'object') {
        console.warn('purchaseMargin recebeu objeto:', value);
        value = 0;
      }
      // Converter string vazia para 0
      if (value === '' || value === null || value === undefined) {
        value = 0;
      }
      // Converter o valor formatado para número
      const numericValue = parseCurrencyValue(value / 100);
      onChange({
        value: numericValue,
        name: 'purchaseMargin',
      });
      setPurchaseMargin(numericValue);
    },
    [onChange],
  );

  // componente para deletar uma proposta
  const deleteTodoLoan = (index: number): void => {
    const newTodos = [...formValues.loanDetails];
    newTodos.splice(index, 1);
    setTodos(newTodos);
    onChange({
      name: 'loanDetails',
      value: newTodos,
    });
  };

  const handleSearchProposal = useCallback(
    async (proposalNumber: string) => {
      if (!proposalNumber) {
        setShowAlert({
          show: true,
          type: 'error',
          message: 'Por favor, informe o número da proposta antes de buscar.',
        });
        return;
      }
      if (proposalNumber.length !== 7) {
        setShowAlert({
          show: true,
          type: 'error',
          message: 'A proposta deve ter 7 dígitos numéricos.',
        });
        return;
      }

      setIsLoading(true);

      try {
        const response: any = await ProposalService.getProposalByNumber(proposalNumber.toString());

        // Verificar se a response é um erro (quando dá 400, 404, etc.)
        if (!response || response.isAxiosError || (response.response && response.response.status >= 400)) {
          throw response;
        }

        // Se chegou aqui, é sucesso - usar response.data
        const data = response.data;

        // Mapear os campos da resposta da API
        const proposalData = {
          name: data.name || '',
          documentNumber: data.document || data.document || '',
          cnpjConsigneeId: data.cnpjConsignee || '',
          phoneNumber: data.phoneNumber.substring(2) || '',
          email: data.email || '',
          proposalNumber: proposalNumber,
        };

        // Validar se é o mesmo cliente (se já existe um cliente original)
        if (originalClientData) {
          const isSameClient =
            originalClientData.name.toLowerCase().trim() === proposalData.name.toLowerCase().trim() &&
            originalClientData.documentNumber === proposalData.documentNumber &&
            originalClientData.cnpjConsigneeId === proposalData.cnpjConsigneeId;

          if (!isSameClient) {
            setShowAlert({
              show: true,
              type: 'error',
              message: 'Proposta inválida',
              description: (
                <p>
                  Para adicionar uma nova proposta, ela deve ter o mesmo <strong>Nome, CPF e convênio</strong> da
                  proposta inicial.
                </p>
              ),
            });
            setIsProposalSearched(false);
            return;
          }
        } else {
          // Se não há cliente original definido e não há propostas já adicionadas,
          // definir este como o cliente original
          if (formValues.loanDetails.length === 0) {
            setOriginalClientData({
              name: proposalData.name,
              documentNumber: proposalData.documentNumber,
              cnpjConsigneeId: proposalData.cnpjConsigneeId,
            });
          }
        }

        // Encontrar o convênio correspondente ao ID
        const selectedConvenant = cnpjconsignees.find((val: any) => val.value == proposalData.cnpjConsigneeId);
        // Atualizar os valores do Form usando setFieldsValue
        form.setFieldsValue({
          name: proposalData.name,
          documentNumber: formatCPF(proposalData.documentNumber),
          cnpjConsigneeId: selectedConvenant ? selectedConvenant.label : '',
          phoneNumber: formatPhoneNumber(proposalData.phoneNumber),
          email: proposalData.email,
          proposalNumber: proposalData.proposalNumber,
        });
        // Atualizar o formValues também através do onChange
        Object.entries(proposalData).forEach(([key, value]) => {
          // Aplicar formatação especial para o telefone
          if (key === 'phoneNumber') {
            onChange({ name: key, value: formatPhoneNumber(value) });
          } else {
            onChange({ name: key, value });
          }
        });

        // Atualizar dados do convênio sem limpar o email que já foi setado
        const convenantData = cnpjconsignees.find((val: any) => val.value == proposalData.cnpjConsigneeId);
        if (convenantData) {
          onChange({
            name: 'hasConsignCard',
            value: convenantData.hasConsignCard || false,
          });
          onChange({
            name: 'limitMultiply',
            value: convenantData.limitMultiply || 0,
          });
          onChange({
            name: 'cnpjConsigneeName',
            value: convenantData.label || '',
          });
        }

        // Marcar que a busca foi realizada para desabilitar os campos
        setIsProposalSearched(true);
        setEnableFields(true);
      } catch (error: any) {
        // Usar o utilitário de tratamento de erros
        const alertConfig = handleProposalError(error);
        // Verificar se deve abrir um modal específico
        if (alertConfig.showModal === true) {
          setShowProposalModal(true);
        } else {
          // Caso contrário, mostrar o alert normal
          console.error('Erro ao buscar proposta:', error);
          setShowAlert({
            show: true,
            type: alertConfig.type,
            message: alertConfig.message,
          });
        }

        setIsProposalSearched(false);
      } finally {
        setIsLoading(false);
      }
    },
    [onChange, form, cnpjconsignees, setCnpjconsignees, formValues.type],
  );

  // Check if proposalNumber exists in array
  const status = formValues.loanDetails.some(function (e: any) {
    return e.proposalNumber == proposalNumber;
  });

  const addTodoLoan = (loanType: string, documentsProposal: any): void => {
    if (todos.length === 9) {
      logger.error('Error on the length of the number');
      setIsSubmitLoading(false);
    } else if (status) {
      setShowAlert({
        show: true,
        type: 'error',
        message: 'Número de proposta já utilizado',
      });
      setIsSubmitLoading(false);
    } else {
      const newTodos: ITodo[] = [...todos, { loanType, proposalNumber, purchaseMargin, documentsProposal }];
      setTodos(newTodos);
      formValues.loanDetails = newTodos;
      setIsSubmitLoading(false);
      setShowAlert({
        show: true,
        type: 'success',
        message: 'Proposta adicionada com sucesso!',
      });
      onChange({
        name: 'loanDetails',
        value: newTodos,
      });

      // Desabilitar campos do cliente após adicionar a primeira proposta
      if (newTodos.length === 1) {
        setDisableClientFields(true);
      }
    }
  };
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSubmitLoading(true);

    // Flag para verificar se a ação foi executada
    let actionExecuted = false;

    const continueFunction = async () => {
      actionExecuted = true;
      let documentsProposal = [];
      try {
        const { data } = await ProposalService.getProposal(proposalNumber);
        documentsProposal = data.documentList;
      } catch (err) {
        console.error('Erro ao buscar proposta:', err);
      }

      await addTodoLoan(loan, documentsProposal);

      // Limpar apenas campos relacionados à proposta individual (proposalNumber, loanType, purchaseMargin)
      // Manter os dados do cliente (phoneNumber, email, companyDocument, companyLegalName) para o PUT
      onChange({ name: 'proposalNumber', value: '' });
      onChange({ name: 'purchaseMargin', value: 0 });
      // Limpar estados locais
      setLoan('');
      setProposalNumber('');
      setPurchaseMargin(0);
      // Limpar o formulário de busca de proposta
      proposalSearchForm.setFieldsValue({
        proposalNumber: '',
      });

      // Limpar campos no formulário principal (apenas selectedLoanType e purchaseMargin)
      form.setFieldsValue({
        selectedLoanType: '',
        purchaseMargin: 0,
      });

      // Resetar estados para permitir nova busca
      setIsProposalSearched(false);
      setEnableFields(false);
    };

    try {
      await validateAndExecuteMarginLimit(continueFunction);
    } catch (error) {
      // Em caso de erro, desativar o loading
      setIsSubmitLoading(false);
      console.error('Erro durante a execução:', error);
    }

    // Verificar após um tempo se a ação foi executada
    // Se não foi, significa que o modal foi fechado sem confirmação
    setTimeout(() => {
      if (!actionExecuted) {
        setIsSubmitLoading(false);
      }
    }, 500);
  };

  const handleChange = useCallback(
    (e: any) => {
      if (e?.target) {
        const { name, value } = e.target;
        onChange({ name, value });
      } else {
        onChange(e);
      }
    },
    [onChange],
  );

  const handleClientNameChange = useCallback(
    (e: any) => {
      const value = e.value;
      onChange({ name: 'name', value });
    },
    [onChange],
  );

  const handleClientConvenantChange = useCallback(
    (e: any) => {
      const value = e.value;
      onChange({ name: 'cnpjConsigneeId', value });
    },
    [onChange],
  );

  useEffect(() => {
    const fetchOptionsCnpjconsignees = async () => {
      try {
        const response = await ProposalService.fetCnpjconsignees();
        if (!response) return;
        const { data } = response;

        const options = data
          .filter((item: any) => item.active)
          .map((item: any) => ({
            value: item.id,
            label: item.description,
            name: 'cnpjConsigneeId',
            hasConsignCard: item.hasConsignCard,
            limitMultiply: item.limitMultiply,
          }));
        setCnpjconsignees(options);
      } catch (error) {
        console.error('Error fetching options cnpjconsignees', error);
        setShowAlert({
          show: true,
          type: 'error',
          message: 'Erro ao buscar convênios. Favor recarregar a página.',
        });
      }
    };

    fetchOptionsCnpjconsignees();
  }, [formValues.type]);

  return (
    <Flex vertical>
      <ModalProposalAlreadyRegistered open={showProposalModal} onClose={() => setShowProposalModal(false)} />
      {showAlert.show && (
        <ProposalAlertWrapper>
          <ProposalAlert
            closable
            onClose={() => setShowAlert({ show: false, message: '', type: 'success' })}
            type={showAlert.type}
            message={showAlert.message}
            description={showAlert.description}
            showIcon
          />
        </ProposalAlertWrapper>
      )}
      <Paginator>2/5</Paginator>
      <TitleStep className="StepTwo">Fale um pouco mais sobre quem está solicitando o empréstimo</TitleStep>
      <SubtitleStep>Insira as informações da proposta Sicred</SubtitleStep>
      <HintStep>
        Informe os dados da proposta para buscar as informações do cliente. Você pode adicionar uma ou mais propostas.
      </HintStep>
      <ProposalForm
        formValues={formValues}
        handleChangeProposalNumber={handleChangeProposalNumber}
        isLoading={isLoading}
        handleSearchProposal={handleSearchProposal}
        proposalSearchForm={proposalSearchForm}
        form={form}
        handleClientNameChange={handleClientNameChange}
        enableFields={enableFields}
        disableClientFields={disableClientFields}
        handleChangeLoan={handleChangeLoan}
        selectedLoanType={selectedLoanType}
        handleChange={handleChange}
        showExtraFields={showExtraFields}
        showEmailForInvoice={showEmailForInvoice}
        showPurchaseMargin={showPurchaseMargin}
        defaultValueConvenant={defaultValueConvenant}
        handleClientConvenantChange={handleClientConvenantChange}
        handleChangePurchaseMargin={handleChangePurchaseMargin}
        purchaseMargin={purchaseMargin}
        isSubmitLoading={isSubmitLoading}
        isButtonEnabled={isButtonEnabled}
        handleSubmit={handleSubmit}
      />
      {formValues.loanDetails.length > 0 && (
        <Flex vertical>
          <Divider size="large" />
          <Text style={{ fontSize: '16px', color: '#101010', marginBottom: '24px' }}>Propostas adicionadas</Text>
        </Flex>
      )}
      {formValues.loanDetails.map((todo: any, index: number) => {
        return <ProposalList key={index} index={index} todo={todo} deleteTodoLoan={deleteTodoLoan} />;
      })}
    </Flex>
  );
};

export default ProposalData;
